import { useEffect, useRef } from 'react';

/**
 * useFokusNachLaden — gibt einem Knopf den Fokus zurueck, den er beim Laden verliert.
 *
 * @description
 *   Ein MUI-Knopf im Ladezustand (`<Button loading>`) traegt **natives
 *   `disabled`** — gemessen am 2026-10-10 im gerenderten DOM (`@mui/material`
 *   7.3.5: `disabled=""`, `tabindex="-1"`, `Mui-disabled`). Und ein deaktiviertes
 *   Element gibt den Fokus an `body` ab (HTML-Fokus-Korrektur). Wer den Knopf
 *   per Tastatur bedient, verliert damit nach dem Ausloesen seinen Platz: der
 *   naechste `Tab` beginnt wieder am Dokumentanfang.
 *
 *   Der Haken merkt sich, ob der Knopf beim EINTRITT in den Ladezustand den
 *   Fokus hatte, und gibt ihn nach dem Ende zurueck — aber nur dann, wenn der
 *   Fokus WEGGEFALLEN ist. Das hat zwei Formen:
 *
 *   1. **Ausserhalb eines Dialogs** gibt der Browser den Fokus an `body` ab.
 *   2. **In einem offenen `Dialog`** faellt der Fokus nicht auf `body`: MUI
 *      spannt eine Fokusfalle (`TrapFocus`) auf und legt ihn auf den
 *      Dialog-Container (`tabindex="-1"`) — einen VORFAHREN des Knopfes
 *      (gemessen am 2026-10-10 in jsdom, `@mui/material` 7.3.5). Fuenf der
 *      sieben K-F1-Stellen liegen in einem Dialog und sehen genau diesen Weg;
 *      die erste Fassung des Hakens kannte nur `body` und griff dort nicht.
 *
 *   Hat der Leser den Fokus waehrend des Laufs dagegen selbst woandershin
 *   bewegt — auf ein Bedienelement, das den Knopf nicht enthaelt —, bleibt er
 *   dort: der Haken holt niemanden zurueck, der nicht weggefallen ist. Auch
 *   ein GESCHWISTER-Knopf im selben Dialog-Container zaehlt dazu.
 *
 *   🔴 Der Fokus wird WAEHREND DES RENDERNs gelesen, nicht im Effekt. React hat
 *   das DOM in dieser Phase noch nicht angefasst, ein fokussierter Knopf ist
 *   also noch fokussiert. Nach dem Commit traegt er `disabled` und der Browser
 *   hat den Fokus bereits an `body` abgegeben — im Effekt waere die Information
 *   unwiederbringlich weg. Genau daran scheitert die naheliegende Fassung, die
 *   den Zustand in einem `useEffect` festhaelt.
 *
 * @useWhen
 *   - Ein beschrifteter Knopf loest eine asynchrone Arbeit aus und traegt
 *     waehrenddessen `loading` (MUI) — dann steht hier der Fokusverlust bevor
 *   - Eine Flaeche ist ueber die Tastatur bedienbar und soll es bleiben
 *
 * @dontUseFor
 *   - `ProgressButton` — der sperrt sich selbst und traegt `aria-busy`; sein
 *     Aufrufer braucht diesen Haken nur, wenn der Knopf per Tastatur erreichbar
 *     ist und der Fokus nach dem Lauf zurueckkommen soll (er tut es dort
 *     ebenfalls ueber diesen Haken)
 *   - Ein `IconButton` ohne Beschriftung — `RefreshButton` kapselt denselben
 *     Gedanken in seinem eigenen Baustein
 *   - Ein Knopf, der beim Laden NICHT deaktiviert wird — dann faellt kein Fokus
 *     weg und der Haken ist ein Leerlauf
 *
 * @related
 *   - RefreshButton — traegt `aria-busy` selbst; dieselbe Zusage am Icon-Knopf
 *   - ProgressButton — der Fortschrittsknopf, an dem derselbe Fokusverlust auftritt
 *
 * @reuseHints
 *   - Jeder Formular-Absende-Knopf, der beim Senden `loading` traegt
 *   - Dialog-Bestaetigungen, die waehrend des Schreibens sperren
 *
 * @props
 *   - ref — React-Ref auf das Knopf-Element (nicht auf einen Halter darum)
 *   - laedt — true, solange die Arbeit laeuft; der Zustand, in dem der Knopf sperrt
 */
export default function useFokusNachLaden(ref, laedt) {
  const hatteFokus = useRef(false);
  const warLaedt = useRef(false);

  // 🔴 Renderphase, nicht Effekt — Begruendung im Kopf dieser Datei.
  if (laedt && !warLaedt.current) {
    hatteFokus.current = ref.current != null && ref.current === document.activeElement;
  }
  warLaedt.current = laedt;

  useEffect(() => {
    if (laedt) return;
    if (!hatteFokus.current) return;
    hatteFokus.current = false;
    // Nur zurueckholen, wenn der Fokus WEGGEFALLEN ist. Steht er noch auf einem
    // anderen Bedienelement, hat der Leser ihn selbst dorthin bewegt.
    const aktiv = document.activeElement;
    // 🔴 Zwei Formen des Wegfallens. Ausserhalb eines Dialogs gibt der Browser
    // den Fokus an `body` ab. In einem offenen `Dialog` spannt MUI eine
    // Fokusfalle (`TrapFocus`) auf und legt ihn auf den Dialog-Container
    // (`tabindex="-1"`) — einen VORFAHREN des Knopfes, nicht `body` (gemessen
    // am 2026-10-10 in jsdom, `@mui/material` 7.3.5). Beide Faelle holen den
    // Fokus zurueck; die zweite Bedingung verlangt ausdruecklich, dass der
    // Knopf im Fokusziel liegt, damit ein bewusst auf einen GESCHWISTER-Knopf
    // bewegter Fokus (derselbe Dialog-Container) nicht zurueckgerissen wird.
    const fokusWeggefallen = aktiv == null || aktiv === document.body;
    const fokusInFalle = aktiv !== ref.current && aktiv.contains(ref.current);
    if (fokusWeggefallen || fokusInFalle) {
      ref.current?.focus();
    }
  }, [laedt, ref]);
}
